import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { updatePermission } from '../../services/api';
import type { PermissionResponse } from '../../services/api/types.gen';

type Props = {
  permission: PermissionResponse;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
};

const EditPermissionDialog: React.FC<Props> = ({
  permission,
  open,
  onClose,
  onUpdated,
}) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(permission.name);
    setDescription(permission.description);
  }, [open, permission]);

  const isFormValid = name.trim() !== '' && description.trim() !== '';

  const handleUpdate = async () => {
    if (!isFormValid) return;
    setIsSaving(true);
    try {
      await updatePermission({
        path: { permission_id: permission.id },
        body: {
          name: name.trim(),
          description: description.trim(),
        },
      });
      showSuccess(
        'Permission Updated',
        `Permission "${name.trim()}" was updated successfully.`
      );
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to update permission:', error);
      showError(
        'Failed to Update Permission',
        'Unable to update permission. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Edit Permission</DialogTitle>
        </DialogHeader>

        <div className='space-y-5 py-2'>
          {/* Name */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-perm-name'>Name</Label>
            <Input
              id='edit-perm-name'
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          {/* Slug — read-only, inline */}
          <div className='flex items-center gap-4 text-sm'>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Slug</span>
              <span className='font-mono text-xs'>{permission.slug}</span>
            </div>
          </div>

          {/* Description */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-perm-description'>Description</Label>
            <Textarea
              id='edit-perm-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant='purple'
            onClick={handleUpdate}
            disabled={!isFormValid || isSaving}
          >
            {isSaving ? 'Saving...' : 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPermissionDialog;
